import { ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { fetchScrapEntriesByArea } from '@/core/api';
import { useTenant } from '@/core/hooks/useTenant';
import { LineScrapRow } from './LineScrapRow';
import { cn } from '@/shared/lib/utils';

interface ProductionLine {
  id: string;
  name: string;
  area_id: string;
}

interface AreaScrapSectionProps {
  area: { id: string; name: string; color: string };
  lines: ProductionLine[];
  entryDate: string;
  factoryId: string;
  isExpanded: boolean;
  onToggle: () => void;
}

/**
 * Area section showing total scrap and expandable line rows.
 */
export const AreaScrapSection = ({
  area,
  lines,
  entryDate,
  factoryId,
  isExpanded,
  onToggle,
}: AreaScrapSectionProps) => {
  const { selectedTenantId } = useTenant();

  // Fetch area totals
  const { data: areaEntries = [] } = useQuery({
    queryKey: ['scrap-entries-area', selectedTenantId, factoryId, entryDate, area.id],
    queryFn: () => fetchScrapEntriesByArea(factoryId, entryDate, area.id),
    enabled: !!factoryId && !!area.id,
  });

  const totalLoss = areaEntries.reduce((sum, e) => sum + e.loss_amount, 0);
  const entryCount = areaEntries.length;

  return (
    <div className="border-b last:border-b-0">
      {/* Area header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors text-left"
      >
        <div
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: area.color }}
        />
        <span className="font-semibold">{area.name}</span>

        {/* Area totals - subtle, next to name */}
        {entryCount > 0 && (
          <span className="text-xs text-muted-foreground">
            ({entryCount} · €{totalLoss.toFixed(2)})
          </span>
        )}

        <div className="flex-1" />

        <ChevronRight
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform',
            isExpanded && 'rotate-90'
          )}
        />
      </button>

      {/* Lines with visual hierarchy */}
      {isExpanded && (
        <div className="relative">
          {/* Vertical line connecting to area */}
          <div
            className="absolute left-6 top-0 bottom-0 w-px"
            style={{ backgroundColor: area.color, opacity: 0.3 }}
          />

          <div className="pl-8">
            {lines.map((line, index) => (
              <LineScrapRow
                key={line.id}
                entryDate={entryDate}
                factoryId={factoryId}
                areaId={area.id}
                lineId={line.id}
                lineName={line.name}
                areaColor={area.color}
                isLast={index === lines.length - 1}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
